import { Address } from "../pages/SalesOrders/models";

// Component form mapping for address components
const componentForm: { [key: string]: string } = {
  street_number: "short_name",
  route: "long_name",
  subpremise: "short_name",
  locality: "long_name", // suburb
  administrative_area_level_1: "short_name", // state
  postal_code: "short_name", // postcode
  country: "long_name",
};

export interface AddressComponents {
  addressLine1?: string;
  addressLine2?: string;
  suburb?: string;
  state?: string;
  postcode?: string;
  country?: string;
}

export interface LocationWithComponents {
  lat?: number;
  lng?: number;
  address?: string;
  components?: AddressComponents;
}

export interface GoogleGeocodingResult {
  formatted_address: string;
  address_components: google.maps.GeocoderAddressComponent[];
  geometry: {
    location: {
      lat: number;
      lng: number;
    };
  };
}

export interface MapProps {
  currentAddress: string;
  initialLocation?: LocationWithComponents;
  onLocationSelect: (location: LocationWithComponents) => void;
}

// Function to extract address components from Google Places result
export const extractAddressComponents = (
  addressComponents: google.maps.GeocoderAddressComponent[]
): AddressComponents => {
  let subpremise = "";
  let streetNumber = "";
  let route = "";
  let suburb = "";
  let state = "";
  let postcode = "";
  let country = "";

  // Process each address component
  for (let i = 0; i < addressComponents.length; i++) {
    const component = addressComponents[i];
    const addressType = component.types[0];

    // Get the value from the appropriate field (short_name or long_name)
    const val =
      (component[
        componentForm[addressType] as keyof google.maps.GeocoderAddressComponent
      ] as string) || "";

    switch (addressType) {
      case "subpremise":
        subpremise = val + "/";
        break;
      case "street_number":
        streetNumber = val + " ";
        break;
      case "route":
        route = val;
        break;
      case "locality":
        suburb = val;
        break;
      case "administrative_area_level_1":
        state = val;
        break;
      case "postal_code":
        postcode = val;
        break;
      case "country":
        country = val;
        break;
      default:
        break;
    }
  }

  // Combine street components for addressLine1
  const addressLine1 = subpremise + streetNumber + route;

  return {
    addressLine1,
    addressLine2: "",
    suburb,
    state,
    postcode,
    country,
  };
};

export const combineAddressComponents = (components: AddressComponents) => {
  return [
    components.addressLine1?.trim(),
    components.addressLine2?.trim(),
    components.suburb?.trim(),
    components.state?.trim(),
    components.postcode?.trim(),
    components.country?.trim(),
  ]
    .filter(Boolean)
    .join(", ");
};

export const isMissingAddressComponents = (
  address: Address | AddressComponents
) => {
  return (
    !address.addressLine1 ||
    !address.suburb ||
    !address.state ||
    !address.postcode ||
    !address.country
  );
};

export const isEmptyAddress = (address?: Address | AddressComponents) => {
  return (
    !address ||
    (!address.addressLine1 &&
      !address.addressLine2 &&
      !address.suburb &&
      !address.state &&
      !address.postcode &&
      !address.country)
  );
};
